/**
 * Turning 92 flat symbols into something a person can find one in.
 *
 * ## Why this is in core rather than in each app
 *
 * Three surfaces present the same picker — the web app, the desktop shell that
 * embeds it, and the phone — and the grouping and the search ranking are
 * decisions, not rendering. Three implementations would drift into three
 * answers for "does typing `gold` find GC", and the one that got it wrong would
 * be the one nobody was looking at.
 *
 * Pure, per CLAUDE.md: this module takes an instrument list and a set of
 * symbols the feed actually carries, and returns structure. It reads nothing.
 *
 * ## The shape of the problem
 *
 * The universe is 68 spot FX pairs, 18 futures roots, 3 equities and 3 crypto.
 * A futures trader scrolling to find ES passes sixty-eight currency pairs
 * first, which is what the phone shipped as. So:
 *
 * - **Futures lead**, subdivided by what they are — a trader hunting a contract
 *   knows whether they want an index or a metal.
 * - **Micros nest under their full-size parent.** `MES` is not a product, it is
 *   a size of `ES`, and the master already says so with `parentRoot`. Nesting
 *   turns 18 futures rows into 12 product rows.
 * - **FX subdivides too.** Majors, crosses, exotics is the standard split and
 *   it is derivable from the pair itself. Sixty-eight rows under one heading
 *   would have reproduced the original problem one level down.
 */
import type { AssetClass, Instrument, InstrumentKind } from './types.js';

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/**
 * What the catalog needs to know about an instrument, and nothing more.
 *
 * Declared as its own narrow shape rather than taking `Instrument`, because two
 * callers hold two different representations of the same rows: the API has the
 * master, whose numbers are `Decimal`, and the browser has the JSON of
 * `/v1/instruments`, whose numbers are strings. A picker that could only be
 * built from one of them would have to be built twice.
 *
 * `kind` and `assetClass` are plain strings so a DTO satisfies this without a
 * cast at the boundary. Exhaustiveness over the real unions is preserved by the
 * assertions on `KIND_SECTION` and `FUTURES_GROUP_LABEL` below — adding a
 * variant to either union fails the build here rather than silently dropping
 * its instruments out of every section.
 */
export interface CatalogInput {
  readonly root: string;
  readonly description: string;
  readonly kind: string;
  readonly assetClass: string;
  readonly exchange: string;
  readonly parentRoot?: string | undefined;
  /** Already a string. `Decimal` does not cross the wire. */
  readonly sizeRatio?: string | null | undefined;
}

/** Adapter for the in-process master, whose ratios are `Decimal`. */
export function catalogInput(i: Instrument): CatalogInput {
  return {
    root: i.root,
    description: i.description,
    kind: i.kind,
    assetClass: i.assetClass,
    exchange: i.exchange,
    parentRoot: i.parentRoot,
    sizeRatio: i.sizeRatio ? i.sizeRatio.toFixed() : null,
  };
}

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/**
 * One selectable size of a product. `ES` and `MES` are two sizes of one thing.
 *
 * `ratio` is the master's `sizeRatio` as a string — a display value only. It is
 * never the basis of a calculation here; valuation reads the instrument.
 */
export interface CatalogSize {
  readonly root: string;
  /** `Full` or `Micro`. */
  readonly label: string;
  readonly ratio: string | null;
}

/** A row in the picker. */
export interface CatalogProduct {
  /** The default selection, and what the feed is asked for. */
  readonly root: string;
  /** `E-mini S&P 500`. What a human scans for. */
  readonly description: string;
  readonly kind: string;
  readonly assetClass: string;
  readonly exchange: string;
  /**
   * Every size, largest first, INCLUDING the full-size root.
   *
   * Length 1 for anything with no micro, which is most of the universe — a
   * caller can render the toggle only when there is a choice to make.
   */
  readonly sizes: readonly CatalogSize[];
}

export interface CatalogGroup {
  readonly id: string;
  readonly label: string;
  readonly products: readonly CatalogProduct[];
}

/** A top-level tab or heading. */
export interface CatalogSection {
  readonly id: SectionId;
  readonly label: string;
  readonly groups: readonly CatalogGroup[];
  /** Products across every group, for the caller that wants a count. */
  readonly count: number;
}

export type SectionId = 'futures' | 'fx' | 'stocks' | 'crypto';

export interface Catalog {
  readonly sections: readonly CatalogSection[];
  /** Every product, in section order. The search corpus. */
  readonly products: readonly CatalogProduct[];
}

// ---------------------------------------------------------------------------
// Taxonomy
// ---------------------------------------------------------------------------

/**
 * Section order is deliberate and is not alphabetical.
 *
 * Futures first because that is what the product is for. This is NOT a default
 * trading style in the sense CLAUDE.md forbids — nothing here is selected on
 * anyone's behalf, and a user who pins six FX pairs never sees this order
 * again. It orders a list; it does not decide anything.
 */
const SECTION_ORDER: readonly SectionId[] = ['futures', 'fx', 'stocks', 'crypto'];

const SECTION_LABEL: Readonly<Record<SectionId, string>> = {
  futures: 'Futures',
  fx: 'Forex',
  stocks: 'Stocks',
  crypto: 'Crypto',
};

/**
 * Typed over the real union, so adding an `InstrumentKind` fails to compile
 * here rather than quietly filing its instruments nowhere.
 *
 * `cfd` maps to null on purpose: a broker's own synthetic has no authoritative
 * spec, so it has no honest home in a taxonomy built from one. Excluded rather
 * than filed wrongly.
 */
const KIND_SECTION: Readonly<Record<InstrumentKind, SectionId | null>> = {
  future: 'futures',
  fx_spot: 'fx',
  stock: 'stocks',
  crypto_spot: 'crypto',
  crypto_perp: 'crypto',
  cfd: null,
};

function sectionOf(kind: string): SectionId | null {
  return KIND_SECTION[kind as InstrumentKind] ?? null;
}

/**
 * Futures subdivide by what the contract is on. Order, again, is not
 * alphabetical — it is roughly how much of the volume each carries.
 *
 * Typed as a complete record of `AssetClass` so a new asset class fails the
 * build instead of producing futures that belong to no group and therefore
 * appear nowhere.
 */
const FUTURES_GROUP_LABEL: Readonly<Record<AssetClass, string>> = {
  equity_index: 'Equity Index',
  energy: 'Energy',
  metal: 'Metals',
  rate: 'Rates',
  fx: 'FX Futures',
  agricultural: 'Agricultural',
  equity: 'Single Stock',
  crypto: 'Crypto Futures',
};

const FUTURES_GROUP_ORDER: readonly AssetClass[] = [
  'equity_index', 'energy', 'metal', 'rate', 'fx', 'agricultural', 'equity', 'crypto',
];

/**
 * The eight currencies that make a pair a major or a cross.
 *
 * Convention, not law, and it is the convention every FX desk and platform
 * uses. Anything outside it makes the pair exotic, which is the useful
 * distinction: exotics are wider, thinner, and not what someone means when
 * they say "the majors".
 */
const MAJOR_CURRENCIES: ReadonlySet<string> = new Set(
  ['USD', 'EUR', 'JPY', 'GBP', 'CHF', 'AUD', 'NZD', 'CAD'],
);

export type FxGroupId = 'fx_majors' | 'fx_crosses' | 'fx_exotics';

/**
 * Majors are USD against another major; crosses are two majors without USD;
 * everything else is exotic.
 *
 * Reads the currencies off the root rather than off a table, because a table
 * of 68 pairs is 68 chances to file one wrongly, and the universe grows.
 */
export function fxGroupOf(root: string): FxGroupId {
  const base = root.slice(0, 3).toUpperCase();
  const quote = root.slice(3, 6).toUpperCase();
  const bothMajor = MAJOR_CURRENCIES.has(base) && MAJOR_CURRENCIES.has(quote);
  if (!bothMajor) return 'fx_exotics';
  return base === 'USD' || quote === 'USD' ? 'fx_majors' : 'fx_crosses';
}

const FX_GROUPS: readonly (readonly [FxGroupId, string])[] = [
  ['fx_majors', 'Majors'],
  ['fx_crosses', 'Crosses'],
  ['fx_exotics', 'Exotics'],
];

// ---------------------------------------------------------------------------
// Building
// ---------------------------------------------------------------------------

/** `0.1` -> `Micro`. Anything else is described by its ratio rather than named. */
function sizeLabel(ratio: string | null): string {
  if (ratio === null) return 'Full';
  return 'Micro';
}

/**
 * Build the picker's structure.
 *
 * @param instruments the master, in its own order — used as the tiebreak so
 *   `ES NQ YM RTY` stays in that order rather than becoming alphabetical.
 * @param available the roots the FEED carries, from `/health`. A product the
 *   feed does not carry is omitted: selecting it answers 404 and blanks the
 *   chart, which is worse than not offering it. Pass `null` to skip the filter
 *   entirely — for a caller that is not choosing something to chart.
 */
export function buildCatalog(
  instruments: readonly CatalogInput[],
  available: ReadonlySet<string> | null,
): Catalog {
  const has = (root: string): boolean => available === null || available.has(root);
  const listed = instruments.filter((i) => has(i.root) && sectionOf(i.kind) !== null);

  // Index micros by the parent they point at. `M2K` -> `RTY` does not share a
  // prefix, which is exactly why this reads `parentRoot` rather than the name.
  const microsOf = new Map<string, CatalogInput[]>();
  for (const i of listed) {
    if (!i.parentRoot) continue;
    const kin = microsOf.get(i.parentRoot);
    if (kin) kin.push(i);
    else microsOf.set(i.parentRoot, [i]);
  }

  const products: CatalogProduct[] = [];
  for (const i of listed) {
    // A micro is a size of its parent, not a product — UNLESS the parent is not
    // listed. A feed carrying MES but not ES would otherwise hide MES entirely.
    if (i.parentRoot && listed.some((p) => p.root === i.parentRoot)) continue;

    const kin = microsOf.get(i.root) ?? [];
    products.push({
      root: i.root,
      description: i.description,
      kind: i.kind,
      assetClass: i.assetClass,
      exchange: i.exchange,
      sizes: [
        { root: i.root, label: 'Full', ratio: null },
        ...kin.map((m) => ({
          root: m.root,
          label: sizeLabel(m.sizeRatio ?? null),
          ratio: m.sizeRatio ?? null,
        })),
      ],
    });
  }

  const sections: CatalogSection[] = [];
  for (const id of SECTION_ORDER) {
    const mine = products.filter((p) => sectionOf(p.kind) === id);
    if (mine.length === 0) continue;
    sections.push({
      id,
      label: SECTION_LABEL[id],
      groups: groupsFor(id, mine),
      count: mine.length,
    });
  }

  // Products in SECTION order, not master order: this is the search corpus, and
  // when two rows score equally the futures one should come first.
  return { sections, products: sections.flatMap((s) => s.groups.flatMap((g) => g.products)) };
}

function groupsFor(section: SectionId, products: readonly CatalogProduct[]): CatalogGroup[] {
  if (section === 'futures') {
    return FUTURES_GROUP_ORDER
      .map((assetClass) => ({
        id: `futures_${assetClass}`,
        label: FUTURES_GROUP_LABEL[assetClass],
        products: products.filter((p) => p.assetClass === assetClass),
      }))
      .filter((g) => g.products.length > 0);
  }

  if (section === 'fx') {
    return FX_GROUPS
      .map(([id, label]) => ({
        id,
        label,
        products: products.filter((p) => fxGroupOf(p.root) === id),
      }))
      .filter((g) => g.products.length > 0);
  }

  // Stocks and crypto are small enough that a subdivision would be ceremony.
  return [{ id: section, label: SECTION_LABEL[section], products: [...products] }];
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/**
 * Why a row matched, in the order the results are ranked.
 *
 * Exposed rather than internal because the UI should be able to say so — a
 * result that matched on description reads as a surprise unless the reason is
 * visible next to it.
 */
export type MatchReason = 'exact' | 'size' | 'prefix' | 'word' | 'description';

export interface CatalogMatch {
  readonly product: CatalogProduct;
  /** The size that matched, when the query named one — `mes` selects MES. */
  readonly root: string;
  readonly reason: MatchReason;
}

const RANK: Readonly<Record<MatchReason, number>> = {
  exact: 0, size: 1, prefix: 2, word: 3, description: 4,
};

/**
 * Fold a typed symbol to something comparable.
 *
 * Deliberately NOT `normaliseSymbol` from `aliases.ts`. That function answers
 * "which instrument is this broker's spelling", and it is right to be strict
 * because attaching fills to the wrong contract corrupts a journal. This one
 * answers "what did somebody half-type into a search box", where being generous
 * costs nothing — a wrong row is visible and one keystroke from being right.
 */
function fold(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Rank the catalog against a query.
 *
 * The ordering is the specification:
 *
 * 1. **Exact root.** Typing `ES` puts ES first, never `Micro E-mini S&P`.
 * 2. **An exact size.** `MES` selects the micro of the ES row rather than
 *    matching ES loosely.
 * 3. **Root prefix.** `EU` finds EURUSD before EURJPY only by master order,
 *    which is the honest tiebreak — nothing here knows which is more popular.
 * 4. **A word in the description.** `gold` finds GC; `mini` finds the E-minis.
 * 5. **Anywhere in the description.** The last resort, so `rud` still finds
 *    Crude rather than nothing.
 *
 * Ties break on catalog order, which is section order — so a futures row wins a
 * tie against an exotic currency pair.
 *
 * An empty query returns nothing rather than everything: the caller renders the
 * grouped sections in that case, and returning 92 unranked rows would invite it
 * to render a flat list instead, which is the thing being fixed.
 */
export function searchCatalog(catalog: Catalog, query: string): readonly CatalogMatch[] {
  const q = fold(query);
  if (!q) return [];

  const out: CatalogMatch[] = [];
  for (const product of catalog.products) {
    const match = matchProduct(product, q, query);
    if (match) out.push(match);
  }

  // A stable sort, so equal scores keep catalog order.
  return out
    .map((m, i) => ({ m, i }))
    .sort((a, b) => RANK[a.m.reason] - RANK[b.m.reason] || a.i - b.i)
    .map((x) => x.m);
}

function matchProduct(
  product: CatalogProduct, q: string, raw: string,
): CatalogMatch | null {
  const root = fold(product.root);
  if (root === q) return { product, root: product.root, reason: 'exact' };

  // A size named exactly selects that size. Checked before prefix so `MES`
  // does not first match some unrelated root beginning `mes`.
  const size = product.sizes.find((s) => fold(s.root) === q);
  if (size) return { product, root: size.root, reason: 'size' };

  if (root.startsWith(q)) return { product, root: product.root, reason: 'prefix' };
  if (product.sizes.some((s) => fold(s.root).startsWith(q))) {
    return { product, root: product.root, reason: 'prefix' };
  }

  // Description matching works on the raw query so a multi-word search behaves:
  // `e-mini s&p` should find ES, and folding it to `eminisp` would not.
  const description = product.description.toLowerCase();
  const terms = raw.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length > 0 && terms.every((t) => wordStart(description, t))) {
    return { product, root: product.root, reason: 'word' };
  }
  if (terms.length > 0 && terms.every((t) => description.includes(t))) {
    return { product, root: product.root, reason: 'description' };
  }

  return null;
}

/** True when `term` begins a word — `gold` matches `Gold Futures`, `old` does not. */
function wordStart(haystack: string, term: string): boolean {
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(term, from);
    if (at === -1) return false;
    if (at === 0 || !/[a-z0-9]/.test(haystack[at - 1]!)) return true;
    from = at + 1;
  }
}

// ---------------------------------------------------------------------------
// Where to open
// ---------------------------------------------------------------------------

/**
 * The symbol to chart when nobody has said which.
 *
 * The fall-through is `last viewed` → `first pinned` → `first listed`, and the
 * order is a constraint rather than a preference. CLAUDE.md forbids a default
 * trading style, so AEGIS must not open on ES because the founder trades ES.
 * Every step here is either something the user did or an ordering — none of
 * them is an opinion about what anyone should trade.
 *
 * `first listed` is the catalogue's own order, which puts futures ahead of
 * spot FX. That is the one place a judgement survives, and it is a judgement
 * about a LIST rather than about a trader: the product is built for futures
 * traders, and the alternative is opening on whichever currency pair the feed
 * happened to enumerate first — which is what shipped, and it was TRYJPY.
 *
 * Returns null only when the catalogue is empty, which means the feed carries
 * nothing and there is no chart to draw.
 */
export function defaultSymbol(
  catalog: Catalog,
  opts: { readonly lastViewed?: string | null; readonly pinned?: readonly string[] } = {},
): string | null {
  const selectable = new Set(catalog.products.flatMap((p) => p.sizes.map((s) => s.root)));

  // Checked rather than trusted: a remembered symbol can have left the feed, or
  // be a leftover from a build with a different universe.
  if (opts.lastViewed && selectable.has(opts.lastViewed)) return opts.lastViewed;

  const pin = opts.pinned?.find((r) => selectable.has(r));
  if (pin) return pin;

  return catalog.products[0]?.root ?? null;
}
